import { useState, useCallback, useRef, useEffect } from 'react'
import { Link } from '@inertiajs/react'
import AppLayout from '../../components/AppLayout'
import { useTheme } from '../../contexts/ThemeContext'

// ── 定数 ─────────────────────────────────────────────────────────────────────

const SEASON_ORDER = { '推し旬': 0, '旬': 1, '通常': 2, '避': 3, null: 4, undefined: 4 }
const SEASON_META = {
  '推し旬': { label: '推し旬', bg: '#1e5c3a', color: '#fff' },
  '旬':     { label: '旬',     bg: '#3a7d54', color: '#fff' },
  '通常':   { label: '通常',   bg: '#c8ccc4', color: '#555' },
  '避':     { label: '避',     bg: '#c0463a', color: '#fff' },
}
const RARITY_META = {
  '-2': { label: '定番',   bg: '#3a5a3a', color: '#fff' },
  '-1': { label: '準定番', bg: '#5b7d4f', color: '#fff' },
  '0':  { label: '標準',   bg: '#c8ccc4', color: '#444' },
  '1':  { label: '準珍',   bg: '#c9963f', color: '#fff' },
  '2':  { label: '珍しい', bg: '#c0463a', color: '#fff' },
}
const COLOR_HASH = {
  赤: '#e60033', 黒: '#2b2b2b', 紫: '#65318e', 白: '#f8fbf8', 橙: '#f08300',
  緑: '#3eb370', 薄: '#69b076', 深: '#00552e', 黄: '#ffec47', 茶: '#783c1d',
  桃: '#f09199', 桜: '#fef4f4', 茜: '#b7282e', 肌: '#fce2c4', 若: '#98d98e',
  浅: '#88cb7f', 青: '#00a497', 紺: '#223a70', 菫: '#7058a3', 紅: '#d7003a',
  苗: '#b0ca71', 柳: '#a8c97f', 錆: '#6c3524', 栗: '#762f07', 褐: '#8a3b00',
  金: '#e6b422',
}
const PROBLEM_META = {
  overlap:          { color: '#c0463a', bg: '#fdf0ef' },
  overused:         { color: '#c9963f', bg: '#fdf6ec' },
  avoid_season:     { color: '#c0463a', bg: '#fdf0ef' },
  low_satisfaction: { color: '#888',   bg: '#f5f5f5' },
}

// ── ヘルパーコンポーネント ─────────────────────────────────────────────────

function Badge({ bg, color, children, style = {} }) {
  return (
    <span style={{ fontSize: '10px', padding: '2px 7px', borderRadius: '10px', fontWeight: 500, background: bg, color, whiteSpace: 'nowrap', ...style }}>
      {children}
    </span>
  )
}

function ColorSwatches({ color1, color2 }) {
  const c1 = COLOR_HASH[color1]
  const c2 = COLOR_HASH[color2]
  return (
    <span style={{ display: 'inline-flex', gap: '2px', alignItems: 'center' }}>
      {c1 && <span title={color1} style={{ width: 10, height: 10, borderRadius: '50%', background: c1, display: 'inline-block', border: '1px solid rgba(0,0,0,0.15)', flexShrink: 0 }} />}
      {c2 && <span title={color2} style={{ width: 10, height: 10, borderRadius: '50%', background: c2, display: 'inline-block', border: '1px solid rgba(0,0,0,0.15)', flexShrink: 0 }} />}
    </span>
  )
}

function ScoreDelta({ score, base, size = 13 }) {
  if (score == null) return null
  const pct = Math.round(score * 100)
  const delta = base != null ? pct - Math.round(base * 100) : null
  const color = pct >= 80 ? '#3a7d54' : pct >= 70 ? '#c9963f' : pct >= 60 ? '#e07030' : '#c0463a'
  return (
    <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: size, fontWeight: 600, color }}>
      {pct}
      {delta != null && delta !== 0 && (
        <span style={{ fontSize: size - 3, marginLeft: '3px', color: delta > 0 ? '#3a7d54' : '#c0463a' }}>
          {delta > 0 ? `▲${delta}` : `▼${Math.abs(delta)}`}
        </span>
      )}
    </span>
  )
}

// ── スコア概要カード ──────────────────────────────────────────────────────────

function ScoreCard({ label, value, simValue, C }) {
  const pct = value != null ? Math.round(value * 100) : null
  const simPct = simValue != null ? Math.round(simValue * 100) : null
  const delta = pct != null && simPct != null ? simPct - pct : null
  const color = pct != null
    ? pct >= 80 ? C.greenDark : pct >= 70 ? C.gold : pct >= 60 ? C.carrot : C.tomato
    : C.inkMuted
  const simColor = simPct != null
    ? simPct >= 80 ? C.greenDark : simPct >= 70 ? C.gold : simPct >= 60 ? C.carrot : C.tomato
    : C.inkMuted

  return (
    <div style={{ textAlign: 'center', minWidth: '90px' }}>
      <div style={{ fontSize: '10px', color: C.inkMuted, letterSpacing: '0.1em', marginBottom: '4px' }}>{label}</div>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'center', gap: '6px' }}>
        <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '32px', fontWeight: 600, color, lineHeight: 1 }}>
          {pct ?? '—'}
        </span>
        {simPct != null && (
          <>
            <span style={{ color: C.inkMuted, fontSize: '14px' }}>→</span>
            <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '32px', fontWeight: 600, color: simColor, lineHeight: 1 }}>
              {simPct}
            </span>
          </>
        )}
      </div>
      {delta != null && delta !== 0 && (
        <div style={{ fontSize: '12px', marginTop: '4px', fontWeight: 600, color: delta > 0 ? C.greenDark : C.tomato }}>
          {delta > 0 ? `▲ ${delta} pt` : `▼ ${Math.abs(delta)} pt`}
        </div>
      )}
    </div>
  )
}

// ── 品目1行（スロット構成ビュー）───────────────────────────────────────────

function ItemRow({ item, slotNum, isSelected, isSwapped, swappedTo, onSelectSlot, C }) {
  const { name, season_status, rarity_score, color1, color2, delivery_score, problems = [] } = item
  const dispSeason  = isSwapped ? swappedTo?.season_status  : season_status
  const dispRarity  = isSwapped ? swappedTo?.rarity_score   : rarity_score
  const dispColor1  = isSwapped ? swappedTo?.color1         : color1
  const dispColor2  = isSwapped ? swappedTo?.color2         : color2
  const seasonMeta  = SEASON_META[dispSeason]
  const rarityMeta  = RARITY_META[String(dispRarity)] || RARITY_META['0']

  return (
    <div
      onClick={() => onSelectSlot(slotNum)}
      style={{
        display: 'grid', gridTemplateColumns: '28px 1fr auto',
        gap: '10px', alignItems: 'center',
        padding: '10px 16px',
        background: isSelected ? '#f0f7f2' : isSwapped ? '#f8fcf4' : 'transparent',
        borderLeft: isSelected ? '3px solid #3a7d54' : isSwapped ? '3px solid #8ec49b' : '3px solid transparent',
        borderBottom: '1px solid #f0ede6',
        cursor: 'pointer',
        transition: 'background 0.15s',
      }}
    >
      <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '11px', color: C.inkMuted, textAlign: 'right' }}>
        {String(slotNum).padStart(2, '0')}
      </span>

      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
          <span style={{ fontFamily: "'Noto Sans JP', sans-serif", fontSize: '13px', fontWeight: 500, color: C.ink }}>
            {isSwapped ? swappedTo?.name : name}
          </span>
          {isSwapped && (
            <span style={{ fontSize: '10px', color: '#3a7d54', fontStyle: 'italic' }}>← {name}から変更</span>
          )}
          {seasonMeta  && <Badge bg={seasonMeta.bg}  color={seasonMeta.color}>{dispSeason}</Badge>}
          {rarityMeta  && <Badge bg={rarityMeta.bg}  color={rarityMeta.color}>{rarityMeta.label}</Badge>}
          <ColorSwatches color1={dispColor1} color2={dispColor2} />
        </div>
        {problems.length > 0 && !isSwapped && (
          <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', marginTop: '4px' }}>
            {problems.map((p, i) => (
              <span key={i} style={{ fontSize: '10px', padding: '1px 6px', borderRadius: '8px',
                background: PROBLEM_META[p.type]?.bg || '#f5f5f5',
                color: PROBLEM_META[p.type]?.color || '#888', fontWeight: 500 }}>
                ⚠ {p.label}
              </span>
            ))}
          </div>
        )}
      </div>

      <div style={{ textAlign: 'right', minWidth: '40px' }}>
        {delivery_score != null ? (
          <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '12px',
            color: delivery_score >= 0.8 ? C.greenDark : delivery_score >= 0.7 ? C.gold : delivery_score >= 0.6 ? C.carrot : C.tomato }}>
            {Math.round(delivery_score * 100)}
          </span>
        ) : <span style={{ color: C.inkMuted, fontSize: '12px' }}>—</span>}
      </div>
    </div>
  )
}

// ── 入れ替えピッカー ───────────────────────────────────────────────────────

const SEASON_FILTER_OPTIONS = [
  { key: 'season_only', label: '旬以上' },
  { key: 'all',         label: '全品目' },
]

function ItemPicker({ slot, availableItems, batchLoading, candidateScores, baseDelivery, onSelectItem, selectedItemCode, C }) {
  const [seasonFilter, setSeasonFilter] = useState('season_only')
  const [query, setQuery] = useState('')

  const filtered = availableItems
    .filter(item => {
      if (seasonFilter === 'season_only' && !['推し旬', '旬'].includes(item.season_status)) return false
      if (query) {
        const q = query.toLowerCase()
        return item.name.toLowerCase().includes(q)
      }
      return true
    })
    .map(item => ({ ...item, _sim: candidateScores[item.item_code] || null }))
    .sort((a, b) => {
      // スコアがあれば高い順、なければ旬→珍しさ順
      const aScore = a._sim?.delivery ?? -1
      const bScore = b._sim?.delivery ?? -1
      if (aScore !== bScore) return bScore - aScore
      return (SEASON_ORDER[a.season_status] - SEASON_ORDER[b.season_status]) || (b.rarity_score - a.rarity_score)
    })

  const selectedSim = selectedItemCode ? candidateScores[selectedItemCode] : null
  const currentItem = slot.candidates[0]

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      {/* ヘッダー */}
      <div style={{ padding: '14px 16px', borderBottom: `1px solid ${C.line}`, background: C.paper2, flexShrink: 0 }}>
        <div style={{ fontSize: '11px', color: C.inkMuted, marginBottom: '4px' }}>
          スロット {slot.slot_number} 入れ替え候補
        </div>
        <div style={{ fontFamily: "'Noto Sans JP'", fontSize: '14px', fontWeight: 500, color: C.ink }}>
          {currentItem?.name}
        </div>
        {selectedSim && (
          <div style={{ marginTop: '8px', padding: '8px 12px', background: C.paper, border: `1px solid ${C.line}`, borderRadius: '8px' }}>
            <div style={{ fontSize: '10px', color: C.inkMuted, marginBottom: '4px' }}>選択中のシミュレーション結果</div>
            <div style={{ display: 'flex', gap: '20px' }}>
              <div>
                <span style={{ fontSize: '10px', color: C.inkMuted }}>お届け </span>
                <ScoreDelta score={selectedSim.delivery} base={baseDelivery} size={13} />
              </div>
              <div>
                <span style={{ fontSize: '10px', color: C.inkMuted }}>魅力 </span>
                <ScoreDelta score={selectedSim.appeal} base={slot.candidates[0]?.appeal_score} size={13} />
              </div>
            </div>
          </div>
        )}
        {batchLoading && (
          <div style={{ marginTop: '8px', fontSize: '11px', color: C.inkMuted }}>スコア計算中...</div>
        )}
      </div>

      {/* フィルター */}
      <div style={{ padding: '10px 16px', borderBottom: `1px solid ${C.lineSoft}`, flexShrink: 0,
        display: 'flex', gap: '8px', alignItems: 'center', background: C.paper }}>
        <div style={{ display: 'flex', gap: '4px' }}>
          {SEASON_FILTER_OPTIONS.map(opt => (
            <button key={opt.key} onClick={() => setSeasonFilter(opt.key)}
              style={{ fontSize: '11px', padding: '3px 10px', borderRadius: '12px',
                border: `1px solid ${seasonFilter === opt.key ? C.greenDark : C.line}`,
                background: seasonFilter === opt.key ? C.greenPale : C.paper,
                color: seasonFilter === opt.key ? C.greenDark : C.inkSoft,
                cursor: 'pointer', fontWeight: seasonFilter === opt.key ? 600 : 400 }}>
              {opt.label}
            </button>
          ))}
        </div>
        <input
          value={query} onChange={e => setQuery(e.target.value)}
          placeholder="品目名で検索"
          style={{ flex: 1, fontSize: '12px', padding: '4px 10px', borderRadius: '8px',
            border: `1px solid ${C.line}`, background: C.paper, color: C.ink, outline: 'none' }}
        />
      </div>

      {/* リスト */}
      <div style={{ flex: 1, overflowY: 'auto' }}>
        {filtered.length === 0 && (
          <div style={{ padding: '24px', textAlign: 'center', color: C.inkMuted, fontSize: '13px' }}>
            該当品目なし
          </div>
        )}
        {filtered.map((item, idx) => {
          const isSelected = selectedItemCode === item.item_code
          const seasonMeta = SEASON_META[item.season_status]
          const rarityMeta = RARITY_META[String(item.rarity_score)] || RARITY_META['0']
          const sim        = item._sim
          const isTop      = idx === 0 && sim != null
          return (
            <div
              key={item.item_code}
              onClick={() => onSelectItem(item)}
              style={{
                padding: '10px 16px', borderBottom: `1px solid ${C.lineSoft}`,
                cursor: 'pointer',
                background: isSelected ? '#f0f7f2' : isTop && !batchLoading ? '#f6fbf8' : 'transparent',
                borderLeft: isSelected ? '3px solid #3a7d54' : isTop && !batchLoading ? '3px solid #8ec49b' : '3px solid transparent',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                {isTop && !batchLoading && (
                  <span style={{ fontSize: '9px', padding: '1px 5px', borderRadius: '6px', background: '#3a7d54', color: '#fff', fontWeight: 600 }}>
                    Best
                  </span>
                )}
                <span style={{ fontSize: '13px', fontWeight: 500, color: C.ink }}>{item.name}</span>
                {seasonMeta && <Badge bg={seasonMeta.bg} color={seasonMeta.color}>{item.season_status}</Badge>}
                {rarityMeta && <Badge bg={rarityMeta.bg} color={rarityMeta.color}>{rarityMeta.label}</Badge>}
                <ColorSwatches color1={item.color1} color2={item.color2} />
                {item.would_overlap && (
                  <Badge bg={PROBLEM_META.overlap.bg} color={PROBLEM_META.overlap.color}>⚠ 前回重複</Badge>
                )}
                {/* 予測スコア */}
                {sim && !batchLoading ? (
                  <span style={{ marginLeft: 'auto', display: 'flex', gap: '10px', alignItems: 'center' }}>
                    <span style={{ fontSize: '10px', color: C.inkMuted }}>お届け</span>
                    <ScoreDelta score={sim.delivery} base={baseDelivery} size={12} />
                  </span>
                ) : batchLoading ? (
                  <span style={{ marginLeft: 'auto', fontSize: '10px', color: C.inkMuted }}>…</span>
                ) : null}
              </div>
              <div style={{ display: 'flex', gap: '12px', marginTop: '4px', fontSize: '11px', color: C.inkMuted }}>
                {item.planning_frequency && <span>理想 {item.planning_frequency}週に1回</span>}
                {item.recent_count > 0 && <span>直近 {item.recent_count}回使用</span>}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

// ── メインコンポーネント ───────────────────────────────────────────────────

export default function Show({ current_week, set_plan, base_score, slot_groups = [], available_items = [] }) {
  const { theme: C } = useTheme()

  const [pendingSwaps,    setPendingSwaps]    = useState({})
  const [selectedSlot,    setSelectedSlot]    = useState(null)
  const [pickerItemCode,  setPickerItemCode]  = useState(null)
  const [simScore,        setSimScore]        = useState(null)   // { delivery, appeal } after confirmed swaps
  const [candidateScores, setCandidateScores] = useState({})    // { [item_code]: { delivery, appeal } }
  const [batchLoading,    setBatchLoading]    = useState(false)
  const batchAbortRef = useRef(null)

  const csrf = () => document.querySelector('meta[name="csrf-token"]')?.content || ''

  // ── 現在の品目リスト（pending swaps 適用後）を構築 ──────────────────────
  const buildItemsPayload = useCallback((swaps) => {
    return slot_groups.map(slot => {
      const base    = slot.candidates[0]
      const swapped = swaps[slot.slot_number]
      if (!swapped) return base
      return {
        ...base,
        name:                    swapped.name,
        season_status:           swapped.season_status,
        rarity_score:            swapped.rarity_score,
        vegtype_flag:            swapped.vegtype_flag,
        color1:                  swapped.color1,
        color2:                  swapped.color2,
        volume:                  swapped.volume,
        planning_frequency:         swapped.planning_frequency,
        middle_category_code:           swapped.middle_category_code,
        recent_appearance_count: swapped.recent_count,
        recent_appearance_weeks: [],
        previous_overlap_status: swapped.would_overlap ? '同一品目' : null,
        previous_overlap_week:   null,
        satisfaction_score:      swapped.satisfaction_score,
        satisfaction_count:      swapped.satisfaction_count,
        best_one_ratio:          swapped.best_one_ratio,
      }
    })
  }, [slot_groups])

  // ── 候補品目をスロットコンテキストに合わせてペイロードに変換 ─────────────
  const buildCandidatePayload = useCallback((availItem, slotItem) => ({
    item_code:               availItem.item_code,
    name:                    availItem.name,
    season_status:           availItem.season_status,
    rarity_score:            availItem.rarity_score,
    vegtype_flag:            availItem.vegtype_flag,
    color1:                  availItem.color1,
    color2:                  availItem.color2,
    volume:                  availItem.volume,
    planning_frequency:         availItem.planning_frequency,
    middle_category_code:           availItem.middle_category_code,
    recent_appearance_count: availItem.recent_count,
    recent_appearance_weeks: [],
    previous_overlap_status: availItem.would_overlap ? '同一品目' : null,
    previous_overlap_week:   null,
    delivery_days:           slotItem?.delivery_days ?? [],
    satisfaction_score:      availItem.satisfaction_score,
    satisfaction_count:      availItem.satisfaction_count,
    best_one_ratio:          availItem.best_one_ratio,
    slot_number:             slotItem?.slot_number,
  }), [])

  // ── スロット選択時: 全候補を一括シミュレーション ─────────────────────────
  const runBatchSimulate = useCallback(async (slotNum, swaps) => {
    if (batchAbortRef.current) batchAbortRef.current.abort()
    const ctrl = new AbortController()
    batchAbortRef.current = ctrl

    const slot      = slot_groups.find(s => s.slot_number === slotNum)
    const slotItem  = slot?.candidates[0]
    const baseItems = buildItemsPayload(swaps)
    const candidates = available_items.map(item => buildCandidatePayload(item, slotItem))

    setBatchLoading(true)
    setCandidateScores({})
    try {
      const res = await fetch(`/set_planning/${current_week}/${set_plan.id}/batch_simulate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrf(), Accept: 'application/json' },
        body: JSON.stringify({ base_items: baseItems, slot_number: slotNum, candidates }),
        signal: ctrl.signal,
      })
      if (!res.ok) throw new Error()
      const data = await res.json()
      const map = {}
      data.forEach(({ item_code, delivery, appeal }) => { map[item_code] = { delivery, appeal } })
      setCandidateScores(map)
    } catch (e) {
      if (e.name !== 'AbortError') setCandidateScores({})
    } finally {
      setBatchLoading(false)
    }
  }, [slot_groups, available_items, buildItemsPayload, buildCandidatePayload, current_week, set_plan])

  // ── 確定済みスワップのスコア更新 ─────────────────────────────────────────
  const runSimulate = useCallback(async (swaps) => {
    const items = buildItemsPayload(swaps)
    try {
      const res = await fetch(`/set_planning/${current_week}/${set_plan.id}/simulate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrf(), Accept: 'application/json' },
        body: JSON.stringify({ items }),
      })
      if (!res.ok) throw new Error()
      const data = await res.json()
      setSimScore({ delivery: data.delivery, appeal: data.appeal })
    } catch {
      setSimScore(null)
    }
  }, [buildItemsPayload, current_week, set_plan])

  // スロット選択 → batch simulate 開始
  const handleSelectSlot = useCallback((slotNum) => {
    const next = selectedSlot === slotNum ? null : slotNum
    setSelectedSlot(next)
    setPickerItemCode(null)
    setCandidateScores({})
    if (next) runBatchSimulate(next, pendingSwaps)
  }, [selectedSlot, pendingSwaps, runBatchSimulate])

  // ピッカーで品目を選択（スコアは batch 結果から表示済み）
  const handleSelectItem = useCallback((item) => {
    setPickerItemCode(item.item_code)
  }, [])

  // 変更を確定
  const handleApplySwap = useCallback(() => {
    if (!selectedSlot || !pickerItemCode) return
    const item = available_items.find(i => i.item_code === pickerItemCode)
    if (!item) return
    const newSwaps = { ...pendingSwaps, [selectedSlot]: item }
    setPendingSwaps(newSwaps)
    setSelectedSlot(null)
    setPickerItemCode(null)
    setCandidateScores({})
    runSimulate(newSwaps)
  }, [selectedSlot, pickerItemCode, available_items, pendingSwaps, runSimulate])

  // 変更をリセット（スロット単位）
  const handleResetSlot = useCallback((slotNum) => {
    const newSwaps = { ...pendingSwaps }
    delete newSwaps[slotNum]
    setPendingSwaps(newSwaps)
    if (Object.keys(newSwaps).length === 0) setSimScore(null)
    else runSimulate(newSwaps)
    if (selectedSlot === slotNum) setSelectedSlot(null)
  }, [pendingSwaps, selectedSlot, runSimulate])

  const selectedSlotData = slot_groups.find(s => s.slot_number === selectedSlot)
  const hasSwaps         = Object.keys(pendingSwaps).length > 0
  const problemCount     = slot_groups.reduce((n, slot) =>
    n + (pendingSwaps[slot.slot_number] ? 0 : (slot.candidates[0]?.problems?.length > 0 ? 1 : 0)), 0)

  return (
    <AppLayout bgColor={C.bg} maxWidth="none" noPadding>
      {/* パンくず */}
      <div style={{ padding: '16px 24px 0', fontSize: '13px', color: C.inkSoft }}>
        <Link href={`/weekly_evaluations/${current_week}`} style={{ color: C.greenDark, textDecoration: 'none' }}>
          週次評価 {current_week}
        </Link>
        <span style={{ margin: '0 8px', color: C.inkMuted }}>／</span>
        <Link href={`/weekly_evaluations/${current_week}/set_plans/${set_plan.id}`} style={{ color: C.greenDark, textDecoration: 'none' }}>
          {set_plan.set_type_name}
        </Link>
        <span style={{ margin: '0 8px', color: C.inkMuted }}>／</span>
        <span style={{ color: C.ink }}>立案支援</span>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: selectedSlotData ? '1fr 360px' : '1fr', height: 'calc(100vh - 80px)', margin: '12px 0 0' }}>

        {/* ── 左カラム ── */}
        <div style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {/* スコアヘッダー */}
          <div style={{ padding: '16px 24px', background: C.paper, borderBottom: `1px solid ${C.line}`, flexShrink: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
              <div>
                <h1 style={{ fontFamily: "'Noto Serif JP', serif", fontSize: '20px', fontWeight: 500, margin: 0, color: C.ink }}>
                  {set_plan.set_type_name}
                  <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '12px', fontWeight: 400, color: C.inkMuted, marginLeft: '12px' }}>
                    {current_week}
                  </span>
                </h1>
                {problemCount > 0 && (
                  <div style={{ marginTop: '6px', fontSize: '12px', color: C.carrot }}>
                    ⚠ 要注意品目 {problemCount} 件 — クリックして入れ替えを検討
                  </div>
                )}
              </div>
              <div style={{ display: 'flex', gap: '32px', alignItems: 'center' }}>
                <ScoreCard label="お届けスコア" value={base_score?.delivery} simValue={simScore?.delivery} C={C} />
                <ScoreCard label="魅力スコア"   value={base_score?.appeal}   simValue={simScore?.appeal}   C={C} />
                {hasSwaps && (
                  <button
                    onClick={() => { setPendingSwaps({}); setSimScore(null); setSelectedSlot(null) }}
                    style={{ fontSize: '12px', padding: '6px 14px', borderRadius: '6px', border: `1px solid ${C.line}`, background: C.paper2, color: C.inkSoft, cursor: 'pointer' }}
                  >
                    変更をリセット
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* スロットリスト */}
          <div style={{ flex: 1, overflowY: 'auto', background: C.paper }}>
            {slot_groups.length === 0 && (
              <div style={{ padding: '48px', textAlign: 'center', color: C.inkMuted }}>
                評価済みセットがありません
              </div>
            )}
            {slot_groups.length > 0 && (
              <div style={{ padding: '8px 16px', background: C.paper2, borderBottom: `1px solid ${C.lineSoft}`,
                display: 'flex', gap: '16px', fontSize: '11px', color: C.inkMuted }}>
                <span>スロット</span>
                <span>品目（クリックで入れ替えパネルを開く）</span>
                <span style={{ marginLeft: 'auto' }}>スコア</span>
              </div>
            )}
            {slot_groups.map((slot, idx) => {
              const isSwapped = !!pendingSwaps[slot.slot_number]
              const swappedTo = pendingSwaps[slot.slot_number]
              return (
                <div key={slot.slot_number}>
                  <ItemRow
                    item={slot.candidates[0]}
                    slotNum={idx + 1}
                    isSelected={selectedSlot === slot.slot_number}
                    isSwapped={isSwapped}
                    swappedTo={swappedTo}
                    onSelectSlot={() => handleSelectSlot(slot.slot_number)}
                    C={C}
                  />
                  {isSwapped && (
                    <div style={{ padding: '4px 16px 4px 68px', background: '#f8fcf4' }}>
                      <button
                        onClick={() => handleResetSlot(slot.slot_number)}
                        style={{ fontSize: '10px', padding: '2px 10px', borderRadius: '8px', border: `1px solid ${C.line}`, background: C.paper, color: C.inkSoft, cursor: 'pointer' }}
                      >
                        この変更を取り消す
                      </button>
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        </div>

        {/* ── 右カラム: 入れ替えピッカー ── */}
        {selectedSlotData && (
          <div style={{ borderLeft: `1px solid ${C.line}`, background: C.paper, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <div style={{ padding: '10px 16px', borderBottom: `1px solid ${C.line}`, background: C.paper2,
              display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
              <span style={{ fontSize: '12px', fontWeight: 600, color: C.ink }}>入れ替え候補</span>
              <button onClick={() => { setSelectedSlot(null); setCandidateScores({}) }}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.inkMuted, fontSize: '16px', lineHeight: 1 }}>✕</button>
            </div>

            <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
              <ItemPicker
                slot={selectedSlotData}
                availableItems={available_items}
                batchLoading={batchLoading}
                candidateScores={candidateScores}
                baseDelivery={base_score?.delivery}
                onSelectItem={handleSelectItem}
                selectedItemCode={pickerItemCode}
                C={C}
              />
            </div>

            {pickerItemCode && (
              <div style={{ padding: '12px 16px', borderTop: `1px solid ${C.line}`, background: C.paper2, flexShrink: 0 }}>
                <button
                  onClick={handleApplySwap}
                  style={{ width: '100%', padding: '10px', borderRadius: '8px', border: 'none',
                    background: C.greenDark, color: '#fff', fontWeight: 600, fontSize: '13px', cursor: 'pointer' }}
                >
                  この品目で入れ替えを確定
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </AppLayout>
  )
}
